import { ReactNode } from 'react';
import { Control, FieldValues, FormState, UseFormProps, UseFormReturn } from 'react-hook-form';
import { AppButtonProps, ButtonColorSchema } from 'uikit/buttons/button/button.component';

export enum ModalResult {
  Confirm = 1,
  Cancel = 2,
  Closed = 3,
}

export enum ModalSize {
  Small = 424,
  Medium = 552,
  Large = 808,
}

export type PromiseWithModalId<TResult> = Promise<TResult> & {
  readonly modalId: string;
};

export type ModalCompletion = {
  result: ModalResult;
};

export type HookformCompletion<T> =
  | {
      result: ModalResult.Confirm;
      payload: T;
    }
  | {
      result: ModalResult.Cancel | ModalResult.Closed;
      payload?: never;
    };

export type ModalRenderActions = {
  confirm: () => void;
  cancel: () => void;
  close: () => void;
};

export type HookformRenderProps<T extends FieldValues> = {
  control: Control<T>;
};

export type HookformFooterFormApi<T extends FieldValues> = Pick<
  UseFormReturn<T>,
  'handleSubmit' | 'setError' | 'clearErrors' | 'getValues'
> & {
  formState: Pick<FormState<T>, 'isSubmitting' | 'errors' | 'isValid'>;
};

export type HookformCustomFooterActions<T extends FieldValues> = {
  form: HookformFooterFormApi<T>;
  confirm: (payload: T) => void;
  cancel: () => void;
  close: () => void;
};

export type HookformSubmitHandler<T extends FieldValues> = (
  payload: T,
  form: HookformFooterFormApi<T>,
) => Promise<void>;

export type BaseModalOptions = {
  size: ModalSize;
  title?: string;
};

export type FooterButtonProps = Pick<AppButtonProps, 'testId' | 'loadingText' | 'disabled' | 'text'> & {
  colorSchema?: ButtonColorSchema;
  tooltip?: string;
};

export type AlertModalOptions = BaseModalOptions & {
  renderBody: () => ReactNode;
} & (
    | {
        renderFooter: (actions: Pick<ModalRenderActions, 'confirm' | 'close'>) => ReactNode;
        buttonProps?: never;
      }
    | {
        buttonProps: FooterButtonProps;
        renderFooter?: never;
      }
  );

export type ConfirmModalOptions = BaseModalOptions & {
  renderBody: () => ReactNode;
} & (
    | {
        renderFooter: (actions: ModalRenderActions) => ReactNode;
        cancelButton?: never;
        confirmButton?: never;
      }
    | {
        renderFooter?: never;
        cancelButton?: FooterButtonProps;
        confirmButton: FooterButtonProps;
      }
  );

type HookformModalBaseOptions<T extends FieldValues> = BaseModalOptions & {
  formProps?: UseFormProps<T>;
  renderBody: (props: HookformRenderProps<T>) => ReactNode;
};

type HookformStandardFooterOptions<T extends FieldValues> = {
  renderFooter?: never;
  submitHandler: HookformSubmitHandler<T>;
  cancelButton?: FooterButtonProps;
  confirmButton: FooterButtonProps;
};

type HookformCustomFooterOptions<T extends FieldValues> = {
  renderFooter: (actions: HookformCustomFooterActions<T>) => ReactNode;
  submitHandler?: never;
  cancelButton?: never;
  confirmButton: never;
};

export type HookformModalOptions<T extends FieldValues> = HookformModalBaseOptions<T> &
  (HookformStandardFooterOptions<T> | HookformCustomFooterOptions<T>);

export type ModalManager = {
  showAlert: (options: AlertModalOptions) => PromiseWithModalId<ModalCompletion>;
  showConfirm: (options: ConfirmModalOptions) => PromiseWithModalId<ModalCompletion>;
  showHookform: <T extends FieldValues>(options: HookformModalOptions<T>) => PromiseWithModalId<HookformCompletion<T>>;
  hideModal: (modalId: string) => void;
};
